import React from "react";
import { Pumpkin, seeded } from "./sprites";
import { NIGHT } from "./season";

/**
 * A graveyard rising out of the top of the footer: a dead tree with an owl,
 * an iron fence, tombstones — one of them with a ghost that climbs out now and
 * then — and fireflies over the footer itself.
 *
 * The ground is filled with the footer's own top colour, so the landscape and
 * the footer read as one piece.
 */

var GROUND = NIGHT.sky;
var ENGRAVING = "#2A2150";

var GROUND_PATH =
  "M-1200 150 L-1200 116 L0 116 C140 98 260 102 390 114 C520 126 640 100 780 104 " +
  "C920 108 1040 124 1180 112 C1300 102 1390 104 1440 110 L2640 110 L2640 150 Z";

/** Iron railings: bars with spear tips between two rails. */
var FENCE = (function () {
  var bars = "";
  var tips = "";
  var top = 78;
  for (var x = 604; x <= 862; x += 13) {
    bars += "M" + x + " 122 V" + top + " ";
    tips += "M" + (x - 3.2) + " " + (top + 1) + " L" + x + " " + (top - 7) + " L" + (x + 3.2) + " " + (top + 1) + " Z ";
  }
  return { bars: bars, tips: tips };
})();

/** Rounded headstone, base buried below the ground line. */
function headstone(x, top, width, height) {
  var r = width / 2;
  return (
    "M" + x + " " + (top + height) +
    " V" + (top + r) +
    " A" + r + " " + r + " 0 0 1 " + (x + width) + " " + (top + r) +
    " V" + (top + height) + " Z"
  );
}

var FIREFLIES = (function () {
  var rand = seeded(2024);
  var list = [];
  for (var i = 0; i < 12; i++) {
    list.push({
      left: (3 + rand() * 94).toFixed(1) + "%",
      top: Math.round(20 + rand() * 200) + "px",
      duration: (5 + rand() * 4).toFixed(1) + "s",
      delay: (-rand() * 8).toFixed(1) + "s",
    });
  }
  return list;
})();

export default function Graveyard() {
  return (
    <React.Fragment>
      <div className="hw-graveyard" aria-hidden="true">
        <svg viewBox="0 0 1440 150" focusable="false">
          {/* Dead tree */}
          <g fill={GROUND}>
            <path d="M183 118 C186 90 186 64 180 40 L187 40 C193 64 195 90 198 118 Z" />
          </g>
          <g fill="none" stroke={GROUND} strokeLinecap="round">
            <path d="M186 70 C174 60 162 58 148 44" strokeWidth="4" />
            <path d="M189 58 C203 48 213 46 227 30" strokeWidth="4" />
            <path d="M184 46 C178 34 180 24 170 12" strokeWidth="3" />
            <path d="M213 45 C221 42 229 44 238 38" strokeWidth="2.4" />
            <path d="M158 51 C152 47 149 39 145 32" strokeWidth="2.2" />
            <path d="M176 26 C170 22 166 16 164 8" strokeWidth="1.8" />
          </g>
          {/* Owl on the branch, blinking */}
          <g>
            <ellipse cx="222" cy="24" rx="7" ry="9" fill={GROUND} />
            <path d="M215.5 17 L216.5 10 L219.5 15 Z M228.5 17 L227.5 10 L224.5 15 Z" fill={GROUND} />
            <circle className="hw-owl-eye" cx="219.4" cy="20.5" r="2.1" fill={NIGHT.candle} />
            <circle className="hw-owl-eye" cx="224.6" cy="20.5" r="2.1" fill={NIGHT.candle} />
          </g>

          {/* Fence */}
          <g fill={GROUND}>
            <rect x="598" y="86" width="270" height="3.2" />
            <rect x="598" y="110" width="270" height="3.2" />
            <path d={FENCE.tips} />
          </g>
          <path d={FENCE.bars} fill="none" stroke={GROUND} strokeWidth="2.4" />

          {/* The ghost sits behind the big stone and climbs out of it */}
          <g transform="translate(956 40) scale(0.52)">
            <g className="hw-grave-ghost">
              <path
                d="M40 3 C19 3 8 21 8 42 L8 93 L17 85 L26 94 L35 85 L44 94 L53 85 L62 94 L72 86 L72 42 C72 21 61 3 40 3 Z"
                fill={NIGHT.bone}
                opacity="0.92"
              />
              <g fill="#1B1330">
                <ellipse cx="29" cy="40" rx="5" ry="7.5" />
                <ellipse cx="51" cy="40" rx="5" ry="7.5" />
                <ellipse cx="40" cy="59" rx="5.5" ry="7.5" />
              </g>
            </g>
          </g>

          {/* Headstones and crosses */}
          <g fill={GROUND}>
            <path d={headstone(300, 70, 34, 52)} />
            <path d="M397 72 H405 V124 H397 Z M387 82 H415 V89 H387 Z" />
            <path d={headstone(470, 86, 26, 40)} transform="rotate(-9 483 124)" />
            <path d={headstone(950, 58, 54, 70)} />
            <path d="M1056 80 H1063 V126 H1056 Z M1047 90 H1072 V96 H1047 Z" transform="rotate(6 1060 126)" />
            <path d={headstone(1146, 88, 28, 36)} />
            <path d={headstone(1272, 76, 30, 40)} transform="rotate(5 1287 116)" />
          </g>
          <text
            x="977"
            y="88"
            textAnchor="middle"
            fill={ENGRAVING}
            style={{ font: "700 11px Manrope, sans-serif", letterSpacing: "1px" }}
          >
            R.I.P.
          </text>
          <path d="M962 96 H992 M966 102 H988" stroke={ENGRAVING} strokeWidth="1.4" />

          <path d={GROUND_PATH} fill={GROUND} />

          {/* A lantern or two left on the graves */}
          <Pumpkin x="1004" y="95" width="30" height="28" faceDelay={-1.3} />
          <Pumpkin x="262" y="88" width="24" height="22" faceDelay={-0.4} />
        </svg>
      </div>

      <div className="hw-fireflies" aria-hidden="true">
        {FIREFLIES.map((fly, index) => (
          <span
            key={index}
            className="hw-firefly"
            style={{
              left: fly.left,
              top: fly.top,
              animationDuration: fly.duration,
              animationDelay: fly.delay,
            }}
          />
        ))}
      </div>
    </React.Fragment>
  );
}
